import { AdViewType } from '@ad-lucy/utils/types/enums';

import type { ImageData, ImageFocus, ImageMeta, ImageMetaObject, Media } from './types';

function createMarkup(html: string): { __html: string } {
  return { __html: html };
}

const isImageMeta = (obj: unknown): obj is ImageMeta => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const requiredKeys: Array<keyof ImageMetaObject> = ['contentType', 'uuid'];

  for (const key of requiredKeys) {
    if (!(key in obj)) return false;
  }

  return true;
};

const getAssetsFilePath = (fileName: string): string => `assets/${fileName}`;
const getAssetsMediaFilePath = (fileName: string): string => getAssetsFilePath(`media/${fileName}`);
const getAssetsCSSFilePath = (fileName: string): string => getAssetsFilePath(`css/${fileName}`);
const getAssetsVastFilePath = (fileName: string): string => getAssetsFilePath(`vast/${fileName}`);

const getImageType = (contentType: string): string => {
  switch (contentType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/svg+xml':
      return 'svg';
    default:
      return contentType.replace('image/', '');
  }
};

const generateImageDataFromImageMeta = (
  tileSize = '',
  imageMeta?: ImageMeta | Media,
  focus: 'left' | 'right' | 'center' | null = null,
): ImageData => {
  if (!isImageMeta(imageMeta) || !imageMeta || typeof imageMeta === 'string') {
    return {
      height: 0,
      src: '',
      width: 0,
      focus: null,
    };
  }

  const { contentType, uuid, url, height = null, width = null } = imageMeta;
  const type = getImageType(contentType);
  const imageData = {
    height,
    width,
    focus,
  };

  if (url) {
    return {
      ...imageData,
      src: url,
    };
  }

  if (focus) {
    return {
      ...imageData,
      src: getAssetsMediaFilePath(`${uuid}.${type}`),
    };
  }

  return {
    ...imageData,
    src: getAssetsMediaFilePath(`${uuid}${tileSize}.${type}`),
  };
};

const isValidImageMeta = (imageMeta?: ImageMeta): boolean =>
  !!imageMeta && typeof imageMeta !== 'string' && !!imageMeta?.uuid;

const getImageData = ({
  adViewType,
  imageL,
  imageM,
  imageS,
  imageBillboard,
  tileSize = '',
  dynamicScaling,
  focus = { imageL: null, imageM: null, imageS: null },
}: {
  adViewType: AdViewType;
  imageL?: ImageMeta;
  imageM?: ImageMeta;
  imageS?: ImageMeta;
  imageBillboard?: ImageMeta;
  tileSize?: string;
  dynamicScaling?: boolean;
  focus?: { imageL: ImageFocus; imageM: ImageFocus; imageS: ImageFocus };
}): ImageData => {
  const enabledFocus = dynamicScaling ? focus : { imageL: null, imageM: null, imageS: null };

  if (adViewType === AdViewType.BILLBOARD) {
    if (isValidImageMeta(imageBillboard)) {
      return generateImageDataFromImageMeta(tileSize, imageBillboard, null);
    }
  }

  if (adViewType === AdViewType.MOBILE) {
    if (isValidImageMeta(imageS)) {
      return generateImageDataFromImageMeta(tileSize, imageS, enabledFocus.imageS);
    }

    if (isValidImageMeta(imageM)) {
      return generateImageDataFromImageMeta(tileSize, imageM, enabledFocus.imageM);
    }
  }

  if (adViewType === AdViewType.HPA && isValidImageMeta(imageM)) {
    return generateImageDataFromImageMeta(tileSize, imageM, enabledFocus.imageM);
  }

  if (isValidImageMeta(imageL)) {
    return generateImageDataFromImageMeta(tileSize, imageL, enabledFocus.imageL);
  }

  return generateImageDataFromImageMeta();
};

const fetchImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (reason) =>
      reject(
        Object.assign(new Error(`Failed to load image: ${src}`), {
          reason,
          image,
        }),
      );
    image.src = src;
  });
};

const getLogoFile = (
  image: ImageMeta,
  individualLogo = false,
  locationLogo: Media | null | undefined = null,
): string => {
  let imageData: Media | null | undefined | string;
  // if individualLogo is true and logos for individual branches exist, use individual store/branch logo
  // otherwise use brand logo
  if (individualLogo && locationLogo) {
    imageData = locationLogo;
  } else {
    imageData = image;
  }

  return generateImageDataFromImageMeta('', imageData).src;
};

const getMapMarkerSrc = (
  branchMapMarker: Media | null | undefined,
  fallbackMapMarkerSrc: string | undefined,
): string => {
  const branchMapMarkerSrc = generateImageDataFromImageMeta('', branchMapMarker).src;
  return branchMapMarkerSrc || fallbackMapMarkerSrc || '';
};

export const AssetsUtils = {
  createMarkup,
  getAssetsVastFilePath,
  getAssetsMediaFilePath,
  getAssetsCSSFilePath,
  getImageData,
  getImageType,
  getLogoFile,
  fetchImage,
  generateImageDataFromImageMeta,
  getMapMarkerSrc,
};
